import React from 'react';
import { Button, Slider, Tooltip } from 'antd';
import {
  ReloadOutlined,
  DownloadOutlined,
  PlusSquareOutlined,
  LeftCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  RightCircleOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';

interface Props {
  onReload: () => void;
  onRewind: () => void;
  onPlayPause: () => void;
  onForward: () => void;
  onDownload: () => void;
  onStillFrame: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetTransform: () => void;
  onSeekChange: (value: number) => void;
  onSeekCommit: (value: number) => void;
  playedSeconds: number;
  duration: number;
  currentTimeLabel: string;
  durationLabel: string;
  isPlaying: boolean;
  disabled?: boolean;
}

const VideoControlsOverlay: React.FC<Props> = ({
  onReload,
  onRewind,
  onPlayPause,
  onForward,
  onDownload,
  onStillFrame,
  zoomIn,
  zoomOut,
  resetTransform,
  onSeekChange,
  onSeekCommit,
  playedSeconds,
  duration,
  currentTimeLabel,
  durationLabel,
  isPlaying,
  disabled,
}) => {
  const formatSliderTooltip = (value?: number) => {
    if (value === undefined || !Number.isFinite(value) || value < 0) {
      return '0:00';
    }

    const totalSeconds = Math.floor(value);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="absolute bottom-0 left-0 z-10 w-full px-6 py-4 bg-gradient-to-t from-black/70 to-transparent">
      <div className="mb-3 flex items-center gap-3 text-xs text-white">
        <span className="w-10 text-left tabular-nums">{currentTimeLabel}</span>
        <Slider
          className="m-0 flex-1"
          min={0}
          max={duration || 0}
          step={0.1}
          value={playedSeconds}
          onChange={value => onSeekChange(value as number)}
          onChangeComplete={value => onSeekCommit(value as number)}
          disabled={disabled || !duration}
          tooltip={{ formatter: formatSliderTooltip }}
        />
        <span className="w-10 text-right tabular-nums">{durationLabel}</span>
      </div>
      <div className="relative flex items-center w-full">
        {/* Left Button */}

        <Tooltip title="Restart video">
          <button
            onClick={onReload}
            disabled={disabled}
            className="text-gray-400 text-xl hover:text-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed">
            <ReloadOutlined className="w-6 h-6 fill-gray-400 hover:fill-gray-200 transition" />
          </button>
        </Tooltip>
        

        {/* Center Controls */}
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-6">
          <Tooltip title="Back 10 seconds">
            <button
              onClick={onRewind}
              disabled={disabled}
              className="text-gray-400 text-xl hover:text-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed">
              <LeftCircleOutlined className="w-6 h-6 fill-gray-400 hover:fill-gray-200 transition" />
            </button>
          </Tooltip>
          <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
            <Button
              shape="circle"
              size="middle"
              onClick={onPlayPause}
              disabled={disabled}
              className="!bg-red-600 text-xl !border-red-600 hover:!bg-red-500 hover:!border-red-500 flex items-center justify-center"
              icon={isPlaying ? <PauseCircleOutlined className="text-white text-xl" /> : <PlayCircleOutlined className="text-white text-xl" />}
            />
          </Tooltip>

          <Tooltip title="Forward 10 seconds">
            <button
              onClick={onForward}
              disabled={disabled}
              className="text-gray-400 text-xl hover:text-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed">
              <RightCircleOutlined className="ml-2 w-6 h-6 fill-gray-400 hover:fill-gray-200 transition" />
            </button>
          </Tooltip>
        </div>

        {/* Right Button */}
        <div className="ml-auto">
          <Tooltip title="Zoom In">
            <button
              type="button"
              onClick={zoomIn}
              disabled={disabled}
              className="text-gray-400 text-xl hover:text-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed">
              <ZoomInOutlined className="w-6 h-6 fill-gray-400 hover:fill-gray-200 transition" />
            </button>
          </Tooltip>
          &nbsp;
          <Tooltip title="Zoom Out">
            <button
              type="button"
              onClick={zoomOut}
              disabled={disabled}
              className="text-gray-400 text-xl hover:text-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed">
              <ZoomOutOutlined className="w-6 h-6 fill-gray-400 hover:fill-gray-200 transition" />
            </button>
          </Tooltip>
          &nbsp;
          <Tooltip title="Capture still frame">
            <button
              onClick={onStillFrame}
              disabled={disabled}
              className="text-gray-400 text-xl hover:text-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed">
              <PlusSquareOutlined className="w-6 h-6 fill-gray-400 hover:fill-gray-200 transition" />
            </button>
          </Tooltip>
          &nbsp;
          <Tooltip title="Download video">
            <button
              onClick={onDownload}
              disabled={disabled}
              className="text-gray-400 text-xl hover:text-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed">
              <DownloadOutlined className="w-6 h-6 fill-gray-400 hover:fill-gray-200 transition" />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default VideoControlsOverlay;
